// CertTable.jsx 🧠📡 CERT Intelligence Table – Full Sync with IoC Design
import React, { useState, useEffect } from "react";
import axios from "axios";
import CertRow from "./CertRow";
import CertFilters from "./CertFilters";
import CertPagination from "./CertPagination";
import { Toast, ToastContainer } from "react-bootstrap";

const CertTable = ({ userCountryCode }) => {
  const [alerts, setAlerts] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    alerte: false,
    exploited: false,
    workaround: false,
    resolved: false,
    user_tags: false,
    affected_country: "",
    time_range: "",
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  
  const [userTags, setUserTags] = useState([]);

  const [toast, setToast] = useState({ show: false, message: "", variant: "success" });

  const showToast = (message, variant = "success") => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast({ ...toast, show: false }), 3000);
  };

	const fetchData = async (keepExpandedId = null) => {
	  const params = {
		page,
		searchQuery,
		alerte: filters.alerte ? "Yes" : "",
		exploited: filters.exploited ? 1 : 0,
		workaround: filters.workaround ? 1 : 0,
		resolved: filters.resolved ? 1 : 0,
		user_tags: filters.user_tags ? 1 : 0,
		affected_country: filters.affected_country,
		time_range: filters.time_range,
	  };

	  try {
		const res = await axios.get("/api/cert_alerts.php", { params });
		setAlerts(res.data.certs || []);
		setTotalPages(res.data.total_pages || 1);
		setTotalFiltered(res.data.total_filtered || 0);
		setTotalAll(res.data.total_all || 0);
		setUserTags(res.data.user_tags || []);

		if (keepExpandedId !== null) {
		  setExpanded({ [keepExpandedId]: true });
		} else {
		  setExpanded({});
		}
	  } catch (err) {
		console.error("CERT fetch error:", err);
		showToast("Failed to load CERT data", "danger");
	  }
	};

  useEffect(() => {
    fetchData();
  }, [page, searchQuery, filters]);

  const toggleCollapse = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters({
      ...filters,
      [name]: type === "checkbox" ? checked : value,
    });
    setPage(1);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({
      alerte: false,
      exploited: false,
      workaround: false,
      resolved: false,
      user_tags: false,
      affected_country: "",
      time_range: "",
    });
    setSearchQuery("");
    setPage(1);
  };

  const handleDelete = async (id) => {
    try {
      const res = await axios.post("/api/delete_cert.php", { id });
      if (res.data.success) {
        showToast("CERT alert deleted successfully", "success");
        fetchData();
      } else {
        showToast(res.data.error || "Failed to delete alert", "danger");
      }
    } catch (err) {
      console.error("Delete error:", err);
      showToast("Server error while deleting", "danger");
    }
  };

  const handleEditSuccess = (message = "Alert updated!") => {
    fetchData();
    showToast(message, "success");
  };

  return (
    <div className="cert-wrapper mx-auto mt-4">
      <CertFilters
        filters={filters}
        handleChange={handleChange}
        resetFilters={resetFilters}
        searchQuery={searchQuery}
        handleSearch={handleSearch}
        handleCollapseReset={() => setExpanded({})}
        totalCount={totalFiltered}
        totalAll={totalAll}
      />

		<table
		  className="table table-dark table-bordered" style={{ border: "1px solid #333" }}>
		  <thead>
			<tr>
			  {["Date (GMT+1)", "POC", "Vulnerability", "CVSS", "Exploited", "Patch", "Workaround"].map((title, index) => {
				const widths = [200, 110, 550, 110, 110, 110, 110];
				return (
				  <th
					key={index}
					style={{
					  backgroundColor: "#1e1e1e",
					  color: "#f8f9fa",
					  minWidth: `${widths[index]}px`,
					  maxWidth: `${widths[index]}px`,
					  padding: "10px",
					  verticalAlign: "middle",
					}}
				  >
					{title}
				  </th>
				);
			  })}
			</tr>
		  </thead>

		  <tbody>
			{alerts.length > 0 ? (
			  alerts.map((alert) => (
				<CertRow
				  key={alert.id}
				  cert={alert}
				  country={userCountryCode}
				  isExpanded={!!expanded[alert.id]}
				  toggleCollapse={() => toggleCollapse(alert.id)}
				  onDelete={handleDelete}
				  onEdit={handleEditSuccess}
				  onRefresh={fetchData}
				  showToast={showToast}
				  userTags={userTags}
				/>
			  ))
			) : (
			  <tr>
				<td
				  colSpan="7"
				  className="text-center"
				  style={{
					backgroundColor: "#323232",
					border: "1px solid #333",
					padding: "10px",
					color: "#f8f9fa",
				  }}
				>
				  No results found.
				</td>
			  </tr>
			)}
		  </tbody>
		</table>

      <CertPagination
        page={page}
        totalPages={totalPages}
        onPageChange={(newPage) => setPage(newPage)}
      />

      <ToastContainer
		  position="bottom-end"
		  className="p-3"
		  style={{
			position: "fixed",
			bottom: "20px",
			right: "20px",
			zIndex: 9999,
		  }}
		>
		  <Toast show={toast.show} bg={toast.variant} onClose={() => setToast({ ...toast, show: false })} delay={3000} autohide>
			<Toast.Body className="text-white">{toast.message}</Toast.Body>
		  </Toast>
		</ToastContainer>
    </div>
  );
};

export default CertTable;
